import {
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  EventEmitter,
  Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { Table, TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';
import { finalize, forkJoin } from 'rxjs';
import { AccountNoteComponent } from './account-note/account-note.component';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { PanelModule } from 'primeng/panel';
import { AccountNotesResponse, AccountNoteWidget, IExternalUser } from '@core/model';
import { UtcToLocalPipe } from '@mynexus/mynexus-ui-lib';
import { AuthenticationService } from '@core/authentication/services';
import { GetUserProfileCredentialsByEmailPipe } from '@feature/user-details/account-notes/user-credenionals-by-email.directive';
import { NotesService } from '@feature/user-details/account-notes/notes.service';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';

@Component({
  selector: 'app-notes-account-notes',
  templateUrl: './account-notes.component.html',
  styleUrls: ['./account-notes.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    TableModule,
    DialogModule,
    DatePickerModule,
    MultiSelectModule,
    AccountNoteComponent,
    ToastModule,
    TooltipModule,
    PanelModule,
    IconField,
    InputIcon
  ],
  providers: [GetUserProfileCredentialsByEmailPipe]
})
export class AccountNotesComponent {
  @Input() userData!: IExternalUser;
  @ViewChild(AccountNoteComponent) accountNoteComponent?: AccountNoteComponent;
  @ViewChild('dt') dt?: Table;
  @Output() noteSaved = new EventEmitter<boolean>();

  collapsed = true;
  isLoading = false;
  totalRecords = 0;
  showNewNote = false;
  showExistingNote = false;
  maximized = false;
  isEditable = false;
  isExisting = false;
  startingNoteSize = '';
  currentNoteSize = '';
  noteDetails: any;
  notesDetails: AccountNoteWidget[] = [];
  notesDetailsOriginal: AccountNoteWidget[] = [];
  activityTypes: any[] = [];
  selectedActivityTypes: string[] = [];
  requestSources: any[] = [];
  selectedRequestSources: string[] = [];
  createdByList: any[] = [];
  selectedCreatedBy: string[] = [];
  selectedCreatedOnRange: Date[] = [];
  selectedCreatedOnDate: Date | null = null;
  sortField: string = 'createdOn';
  sortOrder: number = 1;
  selectedActivityType: string | null = null;
  selectedRequestSource: string | null = null;
  createdBySearch = '';
  createdOnDate: Date | null = null;
  noteSearch: string = '';
  cols = [
    { field: 'activityType', header: 'Activity Type' },
    { field: 'requestSource', header: 'Request Source' },
    { field: 'note', header: 'Note' },
    { field: 'createdBy', header: 'Created By' },
    { field: 'createdOn', header: 'Created On' }
  ];

  constructor(
    private cd: ChangeDetectorRef,
    private notesService: NotesService,
    private messageService: MessageService,
    protected readonly authenticationService: AuthenticationService,
    private userProfileCredentialsByEmail: GetUserProfileCredentialsByEmailPipe,
    private utcToLocalWithSecondsPipe: UtcToLocalPipe
  ) {}

  expandPanel(collapsed: boolean | undefined): void {
    if (!collapsed) {
      this.displayNotes();
    }
  }

  openNotes(): void {
    this.showNewNote = true;
    this.isEditable = true;
    this.isExisting = false;
    if (this.showExistingNote) {
      this.onCloseExistingDialog();
    }
  }

  refreshNotes(): void {
    if (!this.collapsed) {
      this.displayNotes();
    }
  }

  displayNotes(): void {
    this.isLoading = true;
    this.notesService
      .getAccountNotes(this.userData.userId)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cd.detectChanges();
          this.onChangeFilter();
        })
      )
      .subscribe({
        next: (notes: AccountNotesResponse) => {
          this.notesDetails = notes?.length ? [...notes] : [];
          const createdByObservables = notes.map((note: any) =>
            this.userProfileCredentialsByEmail.transform(note.createdBy)
          );
          forkJoin(createdByObservables).subscribe((resolvedUsers: any) => {
            this.notesDetailsOriginal = notes.map((note: any, index: any) => ({
              ...note,
              createdOn: this.utcToLocalWithSecondsPipe.transform(note.createdOn),
              createdBy: resolvedUsers[index]
            }));
            this.notesDetails = notes.map((note: any, index: any) => ({
              ...note,
              createdOn: this.utcToLocalWithSecondsPipe.transform(note.createdOn),
              createdBy: resolvedUsers[index]
            }));

            const uniqueTypes = [
              ...new Set(this.notesDetailsOriginal.map(note => note.activityType))
            ];
            this.activityTypes = uniqueTypes.map(type => ({ label: type, value: type }));

            const uniqueRequestSources = [
              ...new Set(this.notesDetailsOriginal.map(note => note.requestSource))
            ];
            this.requestSources = uniqueRequestSources.map(source => ({
              label: source,
              value: source
            }));

            const uniqueCreatedBy = [...new Set(resolvedUsers)];
            this.createdByList = uniqueCreatedBy.map(user => ({ label: user, value: user }));
            this.sortField = 'createdOn';
            this.sortOrder = 1;
            if (this.dt) {
              this.dt.sortField = this.sortField;
              this.dt.sortOrder = this.sortOrder;

              this.dt.sort({
                field: this.sortField,
                order: this.sortOrder
              });
            }
          });
        },
        error: () => {
          this.notesDetails = [];
          this.notesDetailsOriginal = [];
        }
      });
  }

  onChangeFilter(): void {
    if (
      this.selectedActivityTypes.length === 0 &&
      this.selectedRequestSources.length === 0 &&
      this.selectedCreatedBy.length === 0 &&
      !this.selectedCreatedOnDate
    ) {
      this.notesDetails = [...this.notesDetailsOriginal];
      return;
    }

    let filtered = [...this.notesDetailsOriginal];

    if (this.selectedActivityTypes.length > 0) {
      filtered = filtered.filter(note => this.selectedActivityTypes.includes(note.activityType));
    }

    if (this.selectedRequestSources.length > 0) {
      filtered = filtered.filter(note => this.selectedRequestSources.includes(note.requestSource));
    }

    if (this.selectedCreatedBy.length > 0) {
      filtered = filtered.filter(note => this.selectedCreatedBy.includes(note.createdBy));
    }

    if (this.selectedCreatedOnDate) {
      const selectedDate = new Date(this.selectedCreatedOnDate);

      filtered = filtered.filter(note => {
        const createdDate = new Date(note.createdOn);
        return (
          createdDate.getFullYear() === selectedDate.getFullYear() &&
          createdDate.getMonth() === selectedDate.getMonth() &&
          createdDate.getDate() === selectedDate.getDate()
        );
      });
    }

    this.notesDetails = filtered;
  }

  clearFilters(): void {
    this.selectedActivityTypes = [];
    this.selectedRequestSources = [];
    this.selectedCreatedBy = [];
    this.selectedCreatedOnDate = null;
    this.notesDetails = [...this.notesDetailsOriginal];
    this.sortField = 'createdOn';
    this.sortOrder = 1;
    this.noteSearch = '';
    this.onChangeFilter();
    if (this.dt) {
      this.dt.sortField = this.sortField;
      this.dt.sortOrder = this.sortOrder;

      this.dt.sort({
        field: this.sortField,
        order: this.sortOrder
      });
    }
    if (this.dt) {
      this.dt.clear();
    }
  }

  onMaximize(): void {
    this.maximized = !this.maximized;
    this.setSize(this.maximized ? 'full' : this.startingNoteSize);
  }

  setSize(size: string): void {
    this.currentNoteSize = size;
    this.cd.detectChanges();
  }

  onCloseDialog(): void {
    this.maximized = false;
    this.setSize('sm');
    this.showNewNote = false;
  }

  onCloseExistingDialog(): void {
    this.maximized = false;
    this.setSize('sm');
    this.showExistingNote = false;
    this.accountNoteComponent?.resetForm();
  }

  onDialogVisibilityChange(visible: boolean): void {
    this.showNewNote = visible;
    if (!visible) {
      this.onCloseDialog();
    }
  }

  onExistingDialogVisibilityChange(visible: boolean): void {
    this.showExistingNote = visible;
    if (!visible) {
      this.onCloseExistingDialog;
    }
  }

  filterTable(value: any, field: string, matchMode: string): void {
    this.dt?.filter(value, field, matchMode);
  }

  onTextFilter(field: string, value: string): void {
    this.dt?.filter(value, field, 'contains');
  }

  onDateFilter(field: string, value: Date | null): void {
    if (value) {
      this.dt?.filter(value, field, 'equals');
    }
  }

  restrictEditButtonOrMode(note: any): boolean {
    const createdAdmin = this.authenticationService.userEmail;
    const expirationDate = new Date(note.createdOn);
    expirationDate.setDate(expirationDate.getDate() + 1);
    return createdAdmin === note.createdBy && new Date().getTime() < expirationDate.getTime();
  }

  openNoteDialog(noteId: any): void {
    this.isExisting = true;
    if (this.showNewNote) {
      this.onCloseDialog();
    }
    this.notesService.getAccountNoteByNoteId(noteId).subscribe({
      next: (response: any) => {
        this.noteDetails = {
          ...response,
          createdOn: this.utcToLocalWithSecondsPipe.transform(response.createdOn),
          modifiedOn: this.utcToLocalWithSecondsPipe.transform(response.modifiedOn)
        };
        this.isEditable =
          this.restrictEditButtonOrMode(this.noteDetails) &&
          this.noteDetails.activityType != 'Common Domain Email Verification';
        this.showExistingNote = true;
      }
    });
  }

  handleNoteSaved(event: { note: any; result: any; type: string }) {
    this.refreshNotes();
    this.clearFilters();
    this.noteDetails = null;
    this.isExisting = false;
    if (event.type === 'success') {
      this.messageService.add({
        key: 'noteStatus',
        severity: 'success',
        summary: 'Success',
        detail: 'Account note created successfully.',
        life: 5000
      });
      if (
        event.note.activityType === 'Common Domain Email Verification' &&
        (event.note.verifyAccount === 'true' || event.note.verifyAccount === true)
      ) {
        this.noteSaved.emit(true);
      }
    } else if (event.type === 'update') {
      this.messageService.add({
        key: 'noteStatus',
        severity: 'success',
        summary: 'Success',
        detail: 'Account note updated successfully.',
        life: 5000
      });
      if (
        event.note.activityType === 'Common Domain Email Verification' &&
        (event.note.verifyAccount === 'true' || event.note.verifyAccount === true)
      ) {
        this.noteSaved.emit(true);
      }
    }
    this.showNewNote = false;
  }
}
